import { memo, useEffect, useRef } from 'react';
import { useStore } from '../store/store';
import { effectiveZoom } from '../utils/zoom';
import { getSnapState, subscribeSnap } from './snapBus';
import { SNAP_CONFIG } from './snapConfig';
import { GUIDE_KIND } from './snapGeometry';

// Calque de guides, monté une fois par colonne en mode dessin, au-dessus du
// contenu et du calque de drag (zIndex 100 côté CustomDragLayer).
//
// Il ne re-rend JAMAIS pendant un drag : les traits sont des <div> pré-alloués
// dont seuls les styles sont réécrits, sous requestAnimationFrame, à la
// réception d'un événement du bus. C'est ce qui garantit les 60 fps quelle que
// soit la taille du document — le coût par frame est constant, indépendant du
// nombre de box de la page.

const layerStyle = {
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: 101,
};

const lineStyle = {
  position: 'absolute',
  display: 'none',
  opacity: SNAP_CONFIG.guideOpacity,
  willChange: 'transform',
};

const EMPTY_GUIDES = [];
// Deux axes × maxGuidesPerAxis : borne haute du nombre de traits simultanés.
const LINE_SLOTS = Array.from({ length: SNAP_CONFIG.maxGuidesPerAxis * 2 }, (_, index) => index);

const SnapGuidesOverlay = memo(({ parentId }) => {
  const zoomLevel = useStore(({ zoomLevel }) => zoomLevel);
  const landscape = useStore(({ landscape }) => landscape);
  const lineRefs = useRef([]);
  const frameRef = useRef(null);
  const pendingRef = useRef(null);
  // Le zoom peut changer entre deux frames : lu par référence pour ne pas
  // ré-abonner le calque à chaque changement.
  const zoomFactorRef = useRef(1);
  zoomFactorRef.current = effectiveZoom(zoomLevel, landscape) / 100 || 1;

  useEffect(() => {
    const paint = () => {
      frameRef.current = null;
      const state = pendingRef.current;
      const guides =
        state && state.active && state.parentId === parentId ? state.guides : EMPTY_GUIDES;
      // Épaisseur constante à l'écran : un trait de guide reste fin même à
      // 200 % de zoom.
      const stroke = Math.max(SNAP_CONFIG.strokeWidth / zoomFactorRef.current, 0.5);
      lineRefs.current.forEach((element, index) => {
        if (!element) return;
        const guide = guides[index];
        if (!guide) {
          element.style.display = 'none';
          return;
        }
        const length = guide.end - guide.start;
        element.style.display = 'block';
        element.style.backgroundColor =
          guide.kind === GUIDE_KIND.CENTER ? SNAP_CONFIG.guideCenterColor : SNAP_CONFIG.guideColor;
        if (guide.axis === 'x') {
          element.style.left = `${guide.position - stroke / 2}px`;
          element.style.top = `${guide.start}px`;
          element.style.width = `${stroke}px`;
          element.style.height = `${length}px`;
        } else {
          element.style.left = `${guide.start}px`;
          element.style.top = `${guide.position - stroke / 2}px`;
          element.style.width = `${length}px`;
          element.style.height = `${stroke}px`;
        }
      });
    };

    const onSnap = (state) => {
      pendingRef.current = state;
      if (frameRef.current === null) frameRef.current = requestAnimationFrame(paint);
    };

    onSnap(getSnapState());
    const unsubscribe = subscribeSnap(onSnap);
    return () => {
      unsubscribe();
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    };
  }, [parentId]);

  return (
    <div style={layerStyle} aria-hidden='true'>
      {LINE_SLOTS.map((index) => (
        <div
          key={index}
          style={lineStyle}
          ref={(element) => {
            lineRefs.current[index] = element;
          }}
        />
      ))}
    </div>
  );
});

export default SnapGuidesOverlay;
